import React, { useEffect, useState } from "react";
import { Lightbulb, RefreshCw, AlertTriangle, Bug, Heart, Loader2, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ukDay } from "@/lib/time";
import { PageHead, Panel, Empty } from "@/components/portal/crm/kit";
import { ConnieMark } from "@/components/connie/ConnieMark";
import { ConnieText } from "@/components/connie/ConnieText";

const CATS = [
  { key: "idea", label: "Idea", icon: Lightbulb, hint: "Something you'd love Connexis to do" },
  { key: "change", label: "Change request", icon: RefreshCw, hint: "Something to add, remove or work differently" },
  { key: "issue", label: "Something's wrong", icon: AlertTriangle, hint: "A case, a figure or a process that isn't right" },
  { key: "tech", label: "Tech problem", icon: Bug, hint: "A page, button or upload that isn't working" },
  { key: "praise", label: "Praise", icon: Heart, hint: "Tell us what's working — we'll do more of it" },
];
const TONE = { open: "st-sent", triaged: "st-rev", resolved: "st-off", wontfix: "st-neutral" };

/** Help shape Connexis — ideas, changes, issues and tech problems, straight to the owner. Connie answers first. */
export default function HelpShape() {
  const [cat, setCat] = useState("idea");
  const [text, setText] = useState("");
  const [answer, setAnswer] = useState(null);
  const [asking, setAsking] = useState(false);
  const [sending, setSending] = useState(false);
  const [mine, setMine] = useState(null);

  const loadMine = () => api.get("/connie/feedback/mine").then((r) => setMine(r.data.items || [])).catch(() => setMine([]));
  useEffect(() => { loadMine(); }, []);

  const ask = async () => {
    if (text.trim().length < 4) { toast.error("Tell us a little more first"); return; }
    setAsking(true); setAnswer("");
    try { setAnswer((await api.post("/connie/feedback/quick-answer", { category: cat, text })).data.answer || null); }
    catch { setAnswer(null); toast.error("Connie couldn't answer just now — you can still send it below"); }
    finally { setAsking(false); }
  };

  const send = async () => {
    if (text.trim().length < 4) { toast.error("Tell us a little more first"); return; }
    setSending(true);
    try {
      await api.post("/connie/feedback", { category: cat, text, connie_answer: answer || null, page_url: window.location.pathname });
      toast.success("Sent — it's with the Connexis team now. Thank you.");
      setText(""); setAnswer(null); loadMine();
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't send — try again"); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="help-shape-page">
      <PageHead eyebrow="Your voice in the platform" title="Help shape Connexis" testId="help-shape-head"
        lede="Ideas, changes, things that aren't right, tech problems — or what's working. Connie gives you a quick answer first, then it goes straight to the people who build Connexis." />

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] items-start">
        <Panel title="What's it about?" testId="help-shape-form">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {CATS.map(({ key, label, icon: Icon, hint }) => (
              <button key={key} onClick={() => setCat(key)} data-testid={`help-shape-cat-${key}`}
                      className="text-left rounded-[12px] border px-3.5 py-3 transition-colors"
                      style={cat === key ? { borderColor: "var(--ink)", background: "var(--ink)", color: "#fff" }
                        : { borderColor: "var(--line-2)", background: "#fff" }}>
                <Icon className="size-4" style={{ color: cat === key ? "#7DA9FF" : "var(--accent)" }} />
                <div style={{ fontWeight: 700, fontSize: 13.5, marginTop: 8 }}>{label}</div>
                <div style={{ fontSize: 11.5, lineHeight: 1.4, marginTop: 3, color: cat === key ? "rgba(255,255,255,.65)" : "var(--mut)" }}>{hint}</div>
              </button>
            ))}
          </div>
          <label className="rail-t" style={{ display: "block", margin: "22px 0 8px" }}>Tell us in your own words</label>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} maxLength={3000}
                    placeholder="The more detail the better — what you expected, what happened, which case or page."
                    className="w-full rounded-[12px] border px-4 py-3 text-[14px] leading-relaxed focus:outline-none"
                    style={{ borderColor: "var(--line-2)", resize: "vertical" }} data-testid="help-shape-text" />
          <div className="flex flex-wrap gap-2.5 mt-4">
            <button className="btn btn-ghost connie-glow-btn" onClick={ask} disabled={asking || sending} data-testid="help-shape-ask-connie">
              {asking ? <Loader2 className="animate-spin" /> : <Sparkles />} Ask Connie first
            </button>
            <button className="btn btn-primary" onClick={send} disabled={sending} data-testid="help-shape-submit">
              {sending ? <Loader2 className="animate-spin" /> : <Check />} Send it to the Connexis team
            </button>
          </div>
        </Panel>

        <ConnieAnswer answer={answer} asking={asking} />
      </div>

      <div style={{ marginTop: 28 }}>
        <Panel title="What you've sent us" sub="Every report gets a status. When we reply or make the change, you'll hear by email too." pad={false} testId="help-shape-mine">
          {mine === null ? <Empty>Loading…</Empty> : mine.length === 0 ? <Empty testId="help-shape-mine-empty">Nothing yet — your first idea could be the next feature.</Empty> : (
            <div>
              {mine.map((f) => (
                <div key={f.id} className="px-[22px] py-4 border-b last:border-b-0" style={{ borderColor: "var(--line-soft)" }} data-testid={`help-shape-item-${f.id}`}>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <span className={`st ${TONE[f.status] || "st-neutral"}`} data-testid={`help-shape-status-${f.id}`}><i className="sq" />{f.status_label}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-3)" }}>{f.category_label}</span>
                    {f.source === "connie" && <span style={{ fontSize: 11.5, color: "var(--mut)" }}>· passed on by Connie</span>}
                    <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--mut)" }}>{ukDay(f.created_at)}</span>
                  </div>
                  <p style={{ margin: "9px 0 0", fontSize: 13.5, lineHeight: 1.6, color: "var(--ink)" }}>{f.text}</p>
                  {f.reply_note && (
                    <div style={{ marginTop: 10, padding: "10px 14px", borderRadius: 10, background: "var(--accent-soft)", fontSize: 13, lineHeight: 1.55 }}>
                      <b>From the desk:</b> {f.reply_note}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}

function ConnieAnswer({ answer, asking }) {
  return (
    <div className="card" style={{ padding: 24, minHeight: 300 }} data-testid="help-shape-connie-panel">
      <div className="flex items-center gap-3">
        <ConnieMark size={40} state={asking ? "thinking" : answer ? "speaking" : "idle"} orbit={false} />
        <div>
          <div style={{ font: '800 15px/1.1 Archivo' }}>Ask Connie first</div>
          <div style={{ fontSize: 12, color: "var(--mut)", marginTop: 3 }}>Often there&apos;s an answer already — she&apos;ll find it.</div>
        </div>
      </div>
      <div style={{ marginTop: 18 }}>
        {asking ? (
          <div className="connie-bubble"><span style={{ color: "var(--mut)", fontSize: 13 }}>Reading it and checking how Connexis works…</span><span className="connie-caret" /></div>
        ) : answer ? (
          <div className="connie-bubble connie-in" data-testid="help-shape-connie-answer"><ConnieText text={answer} /></div>
        ) : (
          <p style={{ fontSize: 13.5, lineHeight: 1.65, color: "var(--mut)", margin: 0 }}>
            Write what&apos;s on your mind and tap <b style={{ color: "var(--ink)" }}>Ask Connie first</b>. If it&apos;s a how-to,
            she&apos;ll point you to the exact page. If it&apos;s an idea or a problem, send it on — the owner reads every one.
          </p>
        )}
      </div>
    </div>
  );
}
